import { debounce } from '@bimser/core';
import { findMessage } from '../../';
import { IBCMobileTimePickerProps } from 'BCMobileTimePicker/entities';

export const createElementWithClasses = (type: string, ...classes: string[]): HTMLElement => {
	const element = document.createElement(type);
	element.classList.add(...classes);
	return element;
};

export const setAttributesToElement = (
	element: HTMLElement,
	attributes: { [key: string]: string }
): void => {
	for (const key in attributes) {
		element.setAttribute(key, attributes[key]);
	}
};

export const createAmPmDiv = (labelText: string, isSelected: boolean = false): HTMLDivElement => {
	const div = createElementWithClasses('div', 'adm-picker-view-column-item');
	setAttributesToElement(div, {
		'data-selected': isSelected.toString(),
		'aria-hidden': (!isSelected).toString(),
		'aria-label': labelText
	});

	const label = createElementWithClasses('div', 'adm-picker-view-column-item-label');
	label.innerText = labelText;
	div.appendChild(label);

	return div as HTMLDivElement;
};

export const setupAmPmSelection = (
	wheelDiv: HTMLDivElement,
	amDiv: HTMLDivElement,
	pmDiv: HTMLDivElement,
	setIsPm: (value: boolean) => void,
	props: IBCMobileTimePickerProps
): void => {
	wheelDiv.addEventListener('click', e => {
		const targetDiv = e.target as Node;
		let isPmSelected: boolean;
		if (targetDiv === amDiv || amDiv.contains(targetDiv)) {
			isPmSelected = false;
			wheelDiv.style.transform = 'translateY(0px)';
		} else if (targetDiv === pmDiv || pmDiv.contains(targetDiv)) {
			isPmSelected = true;
			wheelDiv.style.transform = 'translateY(-34px)';
		} else {
			return;
		}

		setIsPm(isPmSelected);
		updateAmPmAttributes(isPmSelected, amDiv, pmDiv, props);
	});
};

export const updateAmPmAttributes = (
	isPmSelected: boolean,
	amDiv: HTMLDivElement,
	pmDiv: HTMLDivElement,
	props: IBCMobileTimePickerProps
): void => {
	const selectedDiv = isPmSelected ? pmDiv : amDiv;
	const nonSelectedDiv = isPmSelected ? amDiv : pmDiv;

	const selectedLabel = isPmSelected ? findMessage.get('102778') : findMessage.get('102777');
	const nonSelectedLabel = isPmSelected ? findMessage.get('102777') : findMessage.get('102778');

	setAttributesToElement(selectedDiv, {
		'data-selected': 'true',
		'aria-hidden': 'false',
		'aria-label': selectedLabel
	});

	setAttributesToElement(nonSelectedDiv, {
		'data-selected': 'false',
		'aria-hidden': 'true',
		'aria-label': nonSelectedLabel
	});
};

const selectAmPmDiv = (
	props: IBCMobileTimePickerProps,
	isPm: boolean,
	amDiv: HTMLDivElement,
	pmDiv: HTMLDivElement,
	wheelDiv: HTMLDivElement
) => {
	if (isPm) {
		wheelDiv.style.transform = 'translateY(-34px)';
	} else {
		wheelDiv.style.transform = 'translateY(0px)';
	}

	updateAmPmAttributes(isPm, amDiv, pmDiv, props);
};

export const localizeHourUnits: (
	props: IBCMobileTimePickerProps,
	setIsPm: React.Dispatch<React.SetStateAction<boolean>>,
	isPm: boolean
) => void = debounce((props, setIsPm, isPm) => {
	if (!props.use12Hours) {
		return;
	}

	const tPickers = document.querySelectorAll('.adm-picker .adm-picker-body .adm-picker-view');

	tPickers?.forEach(tPicker => {
		if (tPicker.querySelector('.adm-picker-view-column.am-pm-column')) {
			return;
		}

		const amPmUnitPanel = document.createElement('div');
		amPmUnitPanel.classList.add('adm-picker-view-column', 'am-pm-column');

		const heightMeasureDiv = document.createElement('div');
		heightMeasureDiv.classList.add('adm-picker-view-item-height-measure');
		amPmUnitPanel.appendChild(heightMeasureDiv);

		const wheelDiv = document.createElement('div');
		wheelDiv.classList.add('adm-picker-view-column-wheel');
		wheelDiv.setAttribute('aria-hidden', 'true');
		wheelDiv.style.transform = 'translateY(0px)';

		const amDiv = createAmPmDiv(findMessage.get('102777'), true);
		const pmDiv = createAmPmDiv(findMessage.get('102778'));

		setupAmPmSelection(wheelDiv, amDiv, pmDiv, setIsPm, props);

		wheelDiv.appendChild(amDiv);
		wheelDiv.appendChild(pmDiv);
		amPmUnitPanel.appendChild(wheelDiv);

		const accessibleDiv = document.createElement('div');
		accessibleDiv.classList.add('adm-picker-view-column-accessible');

		const currentDiv = document.createElement('div');
		currentDiv.classList.add('adm-picker-view-column-accessible-current');
		currentDiv.setAttribute('role', 'button');
		currentDiv.setAttribute(
			'aria-label',
			'The current choice is: ' + findMessage.get('102777')
		);
		currentDiv.innerText = '-';
		accessibleDiv.appendChild(currentDiv);

		const amAccessibleDiv = document.createElement('div');
		amAccessibleDiv.classList.add('adm-picker-view-column-accessible-button');
		amAccessibleDiv.setAttribute('role', 'button');
		amAccessibleDiv.setAttribute(
			'aria-label',
			'The current selection is:' + findMessage.get('102777')
		);
		amAccessibleDiv.innerText = '-';
		accessibleDiv.appendChild(amAccessibleDiv);

		const pmAccessibleDiv = document.createElement('div');
		pmAccessibleDiv.classList.add('adm-picker-view-column-accessible-button');
		pmAccessibleDiv.setAttribute('role', 'button');
		pmAccessibleDiv.setAttribute(
			'aria-label',
			'The current selection is:' + findMessage.get('102778')
		);
		pmAccessibleDiv.innerText = '-';
		accessibleDiv.appendChild(pmAccessibleDiv);

		amPmUnitPanel.appendChild(accessibleDiv);
		tPicker.appendChild(amPmUnitPanel);

		selectAmPmDiv(props, isPm, amDiv, pmDiv, wheelDiv);
	});
}, 50);

export const removeAmPmUnit = debounce((): void => {
	const amPmColumns = document.querySelectorAll(
		'.adm-picker .adm-picker-body .adm-picker-view .am-pm-column'
	);
	amPmColumns.forEach(column => {
		column.remove();
	});
}, 50);
